import React from 'react';
import { PageSection, PageSectionVariants, Alert } from '@patternfly/react-core';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getModelTypeLangKey } from '../i18n';

import { ModelType } from '../store/global-types/customization-types';
import { selectIntersectionOfSupportedModelTypes } from '../store/configurationTarget/configurationTargetSelectors';
import { convertModelTypeToSupportedModelType } from '../store/appConfig/appDeploymentInfoTypes';
import { configurationTargetSelectors } from '../store/configurationTarget/configurationTargetSlice';

interface ModelTypeSupportCheckProps {
    modelType: ModelType;
    children: React.ReactNode;
}

const ModelTypeSupportCheck = (props: React.PropsWithChildren<ModelTypeSupportCheckProps>) => {
    const { t } = useTranslation();
    const intersectionOfSupportedModelTypes = useSelector(selectIntersectionOfSupportedModelTypes);
    const supportedModelType = convertModelTypeToSupportedModelType(props.modelType);
    const isModelTypeSupported = supportedModelType ? intersectionOfSupportedModelTypes.includes(supportedModelType) : false;
    const isLoading = useSelector(configurationTargetSelectors.selectIsFetchingConfigurationTargetData);

    // don't flash an unnecessary "<model type> is unavailable" error message to user while we're 
    // loading and app state is still in flux
    if (isLoading && !isModelTypeSupported) {
        return null;
    }

    if (!isModelTypeSupported) {
        return (
            <PageSection variant={PageSectionVariants.dark} padding={{ default: 'noPadding' }}>
                <Alert variant="danger" isInline title={t('error.unsupportedModelType.title', { modelType: t(getModelTypeLangKey(props.modelType)) })}>
                    {t('error.unsupportedModelType.message', { modelType: t(getModelTypeLangKey(props.modelType)) })}
                </Alert>
            </PageSection>
        );
    }

    return <>{props.children}</>;
};

export default ModelTypeSupportCheck; 